// Web Push helper: registra el Service Worker, suscribe al user, sincroniza con backend.
// Uso desde React: `await ensurePushReady(api)` después del login.

import api from "./api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

function arrayBufferToBase64(buf) {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function permissionState() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

let _swRegistration = null;

export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  if (_swRegistration) return _swRegistration;
  try {
    _swRegistration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    return _swRegistration;
  } catch (e) {
    console.warn("[push] SW registration failed", e);
    return null;
  }
}

async function _subscribeOnPushManager(registration) {
  const { data } = await api.get("/notifications/vapid-public-key");
  const applicationServerKey = urlBase64ToUint8Array(data.public_key);
  const sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });
  await api.post("/notifications/subscribe", {
    endpoint: sub.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(sub.getKey("p256dh")),
      auth: arrayBufferToBase64(sub.getKey("auth")),
    },
    user_agent: navigator.userAgent,
  });
  return sub;
}

/**
 * Activa las notificaciones: pide permiso si hace falta, registra el SW, suscribe.
 * Retorna {ok: bool, reason?: string}.
 */
export async function activatePush() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    return { ok: false, reason: permission }; // 'denied' o 'default' (cerró el popup)
  }
  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: "sw_registration_failed" };
  try {
    const existing = await reg.pushManager.getSubscription();
    if (!existing) {
      await _subscribeOnPushManager(reg);
    } else {
      // Re-sincronizar con backend (por si la subscription quedó orfana)
      await api.post("/notifications/subscribe", {
        endpoint: existing.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(existing.getKey("p256dh")),
          auth: arrayBufferToBase64(existing.getKey("auth")),
        },
        user_agent: navigator.userAgent,
      });
    }
    return { ok: true };
  } catch (e) {
    console.error("[push] activate failed", e);
    return { ok: false, reason: "subscribe_failed", error: e?.message };
  }
}

/**
 * Desactiva: unsubscribe del PushManager y avisa al backend.
 */
export async function deactivatePush() {
  if (!isPushSupported()) return { ok: true };
  const reg = await registerServiceWorker();
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch (e) { /* ignore */ }
  try {
    await api.delete("/notifications/subscribe", { data: { endpoint } });
  } catch (e) { /* ignore */ }
  return { ok: true };
}

/**
 * Auto-sync silencioso al login: si el browser ya tiene permission=granted y
 * hay subscription activa, asegura que esté registrada en backend. NO pide permiso.
 */
export async function ensurePushReady() {
  if (!isPushSupported()) return;
  if (Notification.permission !== "granted") return;
  const reg = await registerServiceWorker();
  if (!reg) return;
  try {
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      await api.post("/notifications/subscribe", {
        endpoint: existing.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(existing.getKey("p256dh")),
          auth: arrayBufferToBase64(existing.getKey("auth")),
        },
        user_agent: navigator.userAgent,
      });
    }
  } catch (e) { /* ignore */ }
}

export async function sendTestNotification() {
  const r = await api.post("/notifications/test");
  return r.data;
}
